import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Package, ArrowRight, ClipboardList, History, AlertTriangle, Repeat, Loader2 } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { formLocalToUtc, utcToFormLocal, getNowInPlanningTz } from "@/lib/timezone";
import { OrderHistory } from "@/components/orders/OrderHistory";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { OrderStopsSection } from "@/components/orders/OrderStopsSection";
import { computeDurationWarnings, formatDurationWarning } from "@/lib/order-duration-warning";
import { ChangeReasonDialog } from "@/components/orders/ChangeReasonDialog";
import { useChangeEventPrompt, recordChangeEvents } from "@/hooks/useChangeEventPrompt";
import { detectPivotPrompts, buildPivotInputs } from "@/lib/change-event-pivots";
import { shouldSkipEapi } from "@/lib/eapi-eligibility";
import { REASON_LABELS } from "@/lib/change-events";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useOrderMutations } from "@/hooks/useOrderMutations";
import { useTractorConflictIdsByWindows } from "@/hooks/useTractorConflictIds";
import {
  useTractorOosStatusMap,
  computeOosBlockedIdsForWindow,
} from "@/hooks/useTractorOosStatusMap";
import { useTruckEligibilityMap } from "@/hooks/useTruckEligibility";
import {
  computeEligibilityReasons,
  validateTractorEligibility,
} from "@/lib/truck-eligibility";
import { buildEligibilityChecks } from "@/components/orders/tractorEligibilityChecks";
import {
  buildOosAssignmentChecks,
  validateTractorOosAssignments,
} from "@/lib/tractor-oos-preflight";
import { OrderFormData, LegData } from "@/components/orders/orderFormTypes";
import { validateLegs } from "@/hooks/useOrderFormLegLogic";
import { isOrderFormValid } from "@/lib/order-form-validation";
import { SubmitWithReasons } from "@/components/orders/SubmitWithReasons";
import { hasConfirmAssets, isPerLegAssets, isPerLegTripType, removesConfirmedAssets } from "@/lib/order-form-assets";
import { resolveLegEditLocks, validateLegEdits } from "@/lib/leg-edit-rules";
import { resolveFormLegs } from "@/lib/order-form-legs";
import { TrailerIdField } from "@/components/orders/TrailerIdField";
import { NeedsServiceBlockedDialog } from "@/components/orders/NeedsServiceBlockedDialog";
import { assertTrailerAssignable } from "@/lib/trailer-assignability";
import {
  TrailerCrossTenantDialog,
  findTrailerInOtherTenants,
  type OtherTenantTrailerMatch,
} from "@/components/trailers/TrailerCrossTenantDialog";
import { TractorCombobox } from "@/components/orders/TractorCombobox";
import { DriverCombobox } from "@/components/orders/DriverCombobox";
import {
  useResolvedCheckedInTrailer,
  useSyncInheritedTrailerType,
  customTrailerDefaultSlugFor,
} from "@/components/orders/trailerTypeFieldLogic";
import { CheckedInTrailerTypeOverrideDialog } from "@/components/orders/CheckedInTrailerTypeOverrideDialog";
import {
  Order,
  OrderStatus,
  Customer,
  Tractor,
  Driver,
  Location,
  ORDER_STATUS_LABELS,
} from "@/types/tms";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { PendingBolCheckbox } from "@/components/orders/form/PendingBolCheckbox";
import { tripTypeOptionsForTenant } from "@/lib/feature-flags";
import { FormSection } from "@/components/orders/form/FormSection";
import { AdvancedFieldsToggle } from "@/components/orders/form/AdvancedFieldsToggle";
import { EquipmentAdvancedFields } from "@/components/orders/form/EquipmentAdvancedFields";
import { ShipmentFields } from "@/components/orders/form/ShipmentFields";
import {
  hydrateLegAdvanced,
  hydrateOrderAdvanced,
  hasAnyAdvancedValues,
  validateAdvancedFields,
} from "@/components/orders/form/advancedFields";



const normalizeOrderStatus = (status: unknown): OrderStatus => {
  const normalized = String(status ?? "booked").trim().toLowerCase();
  if (["booked", "confirmed", "in_transit", "delivered", "canceled", "tender_received"].includes(normalized)) {
    return normalized as OrderStatus;
  }
  return "booked";
};

type OrderEditMode = "full" | "assign-driver" | "assign-tractor";

interface OrderEditDialogProps {
  order: Order | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  mode?: OrderEditMode;
}

export function OrderEditDialog({ order, open, onOpenChange, onSuccess, mode = "full" }: OrderEditDialogProps) {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const { activeTenant, tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"details" | "history">("details");
  const [showTractorWarning, setShowTractorWarning] = useState(false);
  const [pendingTractorId, setPendingTractorId] = useState<string | null>(null);
  const [showTrailerWarning, setShowTrailerWarning] = useState(false);
  const [pendingTrailerId, setPendingTrailerId] = useState<string | null>(null);
  const [needsServiceBlock, setNeedsServiceBlock] = useState<{
    trailerId: string;
    reason: string | null;
  } | null>(null);
  // Pivot reason captured from the Change Vehicle / Change Trailer dialog.
  // Forwarded to useOrderMutations as `_pivotReason` and appended to the
  // EAPI cancel-trip reason string ("Truck pivot: <reason>", etc.).
  const PIVOT_REASON_PRESETS = [
    "Exceeds weight limit",
    "Customer request",
    "Inspection fail",
    "INELIGIBLE_FOR_AUTO",
  ] as const;
  const [pivotReasonChoice, setPivotReasonChoice] = useState<string>("");
  const [pivotReasonOther, setPivotReasonOther] = useState<string>("");
  const [pendingPivotReason, setPendingPivotReason] = useState<string | null>(null);
  const resolvedPivotReason = (() => {
    if (!pivotReasonChoice) return "";
    if (pivotReasonChoice === "Other") return pivotReasonOther.trim();
    return pivotReasonChoice;
  })();
  const pivotReasonValid = resolvedPivotReason.length > 0;
  const resetPivotReason = () => {
    setPivotReasonChoice("");
    setPivotReasonOther("");
  };
  const [showConfirmRequiresWarning, setShowConfirmRequiresWarning] = useState(false);
  const [showCannotRemoveWarning, setShowCannotRemoveWarning] = useState(false);
  // ── Historical / billing-only edit mode ──
  // Admins can flip this on when correcting a past order for billing/audit
  // purposes. When on:
  //  - Past-time validation is bypassed (`isOrderFormValid`).
  //  - ALL EAPI / pivot side effects in `useOrderMutations` are skipped
  //    via the `_historicalEdit` flag on the mutation payload.
  //  - Tractor / trailer / trip-type / no-EAPI warning dialogs are
  //    suppressed because they exist to keep the external trip in sync.
  //  - The pivot-reason prompt is skipped (the change isn't a pivot).
  // Defaults to ON when the order is delivered/canceled and the caller is
  // admin — admins almost always reach this dialog for billing fixes on
  // historical orders. The Switch lets them flip it OFF if they truly
  // want the legacy pivot behavior. Hidden entirely for non-admins.
  const [historicalEditUserToggle, setHistoricalEditUserToggle] = useState<boolean>(true);
  // Cross-tenant trailer detection state
  const [crossTenantMatches, setCrossTenantMatches] = useState<OtherTenantTrailerMatch[]>([]);
  const [showCrossTenantDialog, setShowCrossTenantDialog] = useState(false);
  const [skipCrossTenantOnce, setSkipCrossTenantOnce] = useState(false);
  const initialFormDataRef = useRef<string>("");
  const { data: trailerTypes } = useTrailerTypes();
  // Shared dynamic-reason prompt for trip pivots (trip_type / trailer / tractor).
  const { promptForReasons: promptPivotReasons, dialogProps: pivotDialogProps } =
    useChangeEventPrompt();

  const [formData, setFormData] = useState<OrderFormData>({
    order_type: "single-leg",
    legs: [],
    status: "booked",
    ship_from_location_id: "",
    ship_from_street_address: "",
    ship_from_city: "",
    ship_from_province_state: "",
    ship_from_postal_zip_code: "",
    ship_from_country: "",
    ship_to_location_id: "",
    ship_to_street_address: "",
    ship_to_city: "",
    ship_to_province_state: "",
    ship_to_postal_zip_code: "",
    ship_to_country: "",
    ready_time: "",
    requested_delivery_time: "",
    ready_time_end: "",
    requested_delivery_time_end: "",
    billing_party_id: "",
    trailer_id: "",
    trailer_type_id: "",
    tender_id: "",
    assigned_tractor_id: "",
    assigned_operator_id: "",
    assigned_operator_2_id: "",
    trip_type: "",
    is_recurring: false,
    recurrence_days: [],
    recurrence_end_date: "",
  });

  const showAdvanced = formData.show_advanced_fields === true;

  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data } = await supabase.from("customers").select("*").order("organization_name");
      return data as Customer[];
    },
  });

  const { data: locations } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data } = await supabase.from("locations").select("*").eq("archived", false).order("short_code");
      return data as Location[];
    },
  });

  /** AURY + CI escape hatch: API-created orders normally lock origin,
   *  destination, and the ready/delivery time windows. When the billing
   *  party's short_code is "AURY" AND the trip type is "CI", dispatchers
   *  may edit those fields. Reacts live to Trip Type changes in the form. */
  const billingPartyShortCode = useMemo(
    () => customers?.find((c) => c.id === formData.billing_party_id)?.short_code ?? null,
    [customers, formData.billing_party_id],
  );
  const auryCiOverride =
    billingPartyShortCode === "AURY" && formData.trip_type === "CI";

  const { data: tractors } = useQuery({
    queryKey: ["tractors"],
    queryFn: async () => {
      const { data } = await supabase.from("tractors").select("*").order("name");
      return data as Tractor[];
    },
  });

  // Maps opaque ids to readable labels for the "original → pivoted" line in
  // the reason dialog.
  const resolvePivotLabel = useCallback(
    (field: "trip_type" | "trailer_id" | "assigned_tractor_id", value: string | null) => {
      if (!value) return null;
      if (field === "assigned_tractor_id") {
        const t = tractors?.find((x: any) => x.id === value);
        return (t as any)?.name ?? (t as any)?.unit_number ?? null;
      }
      return value;
    },
    [tractors],
  );

  const toUtcWindowPart = useCallback(
    // Order input is always Central; the stop arguments are kept only so the
    // call sites read naturally.
    (local: string, _street?: string, _city?: string): string | null => formLocalToUtc(local),
    [],
  );

  const durationWarnings = useMemo(
    () => computeDurationWarnings(formData, toUtcWindowPart).filter((w) => w.legNumber == null),
    [formData, toUtcWindowPart],
  );

  /** One conflict window per leg (multi-leg) or a single order-level window. */
  const legConflictWindows = useMemo(() => {
    if (formData.order_type === "multi-leg" && formData.legs.length > 0) {
      return formData.legs.map((leg) => ({
        start: toUtcWindowPart(leg.ready_time, leg.ship_from_street_address, leg.ship_from_city),
        end: toUtcWindowPart(
          leg.requested_delivery_time_end || leg.requested_delivery_time,
          leg.ship_to_street_address,
          leg.ship_to_city,
        ),
      }));
    }
    return [
      {
        start: toUtcWindowPart(
          formData.ready_time,
          formData.ship_from_street_address,
          formData.ship_from_city,
        ),
        end: toUtcWindowPart(
          formData.requested_delivery_time_end || formData.requested_delivery_time,
          formData.ship_to_street_address,
          formData.ship_to_city,
        ),
      },
    ];
  }, [formData, toUtcWindowPart]);

  const conflictWindow = useMemo(() => {
    const starts = legConflictWindows.map((w) => w.start).filter(Boolean) as string[];
    const ends = legConflictWindows.map((w) => w.end).filter(Boolean) as string[];
    return {
      start: starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : null,
      end: ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : null,
    };
  }, [legConflictWindows]);

  // Disable tractors already assigned to another active order or leg whose
  // window overlaps. Computed per leg so a conflict on one leg never blocks
  // a truck on a non-overlapping sibling leg.
  const { perWindow: conflictIdsByLeg, union: conflictingTractorIds } =
    useTractorConflictIdsByWindows({
      tenantId,
      windows: legConflictWindows,
      enabled: !!order?.id,
      excludeOrderId: order?.id,
      excludeLegOrderId: order?.id,
    });


  // OOS awareness.
  const { statusById: oosStatusById, recordsById: oosRecordsById } =
    useTractorOosStatusMap();
  // OOS blocking: for multi-leg + "use same tractor", union per-leg helper
  // results so partial windows still block and mid-order-only service
  // windows don't over-block (matches per-leg selector).
  const oosBlockedByLeg = legConflictWindows.map((w) =>
    computeOosBlockedIdsForWindow(oosRecordsById, w.start, w.end),
  );
  const oosBlockedForWindow = new Set<string>();
  oosBlockedByLeg.forEach((set) => set.forEach((id) => oosBlockedForWindow.add(id)));
  const mergedDisabledTractorIds = new Set<string>([
    ...(conflictingTractorIds ?? []),
    ...oosBlockedForWindow,
  ]);
  /** Per-leg disabled ids so a conflict on one leg doesn't block the others. */
  const disabledTractorIdsByLeg = legConflictWindows.map(
    (_w, index) =>
      new Set<string>([
        ...(conflictIdsByLeg[index] ?? []),
        ...(oosBlockedByLeg[index] ?? []),
      ]),
  );


  // Truck eligibility.
  const { eligibilityByTractor, gateState: eligibilityGateState } =
    useTruckEligibilityMap();
  const eligibilityReasonsById = computeEligibilityReasons(
    eligibilityByTractor,
    formData.trip_type,
    (tractors ?? []).map((t) => t.id),
    eligibilityGateState,
  );

  const { data: drivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: async () => {
      const { data } = await supabase.from("drivers").select("*").order("name");
      return data as Driver[];
    },
  });

  const { data: recurringTemplate } = useQuery({
    queryKey: ["recurring-template", order?.recurring_order_template_id],
    queryFn: async () => {
      if (!order?.recurring_order_template_id) return null;
      const { data } = await supabase
        .from("recurring_order_templates")
        .select("unique_id")
        .eq("id", order.recurring_order_template_id)
        .single();
      return data;
    },
    enabled: !!order?.recurring_order_template_id,
  });

  // An order is "multi-leg" when it has more than one row in public.legs —
  // the single source of truth. `leg_count` (attached by useOrdersQuery) and
  // the legacy `leg_sequence === 0` parent sentinel are only used as a hint
  // while the authoritative count is still loading, or when the dialog is
  // opened for a legacy child row (where the count query is skipped).
  const { data: legRowCount } = useQuery({
    queryKey: ["order-edit-leg-count", order?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("legs")
        .select("id", { count: "exact", head: true })
        .eq("order_id", order!.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!order?.id && open && !order?.parent_order_id,
  });
  const isMultiLeg =
    typeof legRowCount === "number"
      ? legRowCount > 1
      : order?.leg_sequence === 0 ||
        (typeof order?.leg_count === "number" && order.leg_count > 1);

  // Derive whether this order has ANY active external trip — either the
  // legacy parent `eapi_trip_id` (single-leg) or one or more active per-leg
  // trips on `public.trips` (multi-leg, where parent `eapi_trip_id` is
  // always null). Every warning/gate below uses this derived value instead
  // of `order?.eapi_trip_id` so multi-leg orders behave identically.
  const { data: activeLegTripsForOrder } = useQuery({
    queryKey: ["active-leg-trips", order?.id],
    enabled: !!order?.id && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trips")
        .select("eapi_trip_id, leg_id, legs!inner(order_id)")
        .eq("is_active", true)
        .eq("legs.order_id", order!.id);
      if (error) throw error;
      return ((data as any[]) ?? [])
        .filter((r) => !!r?.eapi_trip_id)
        .map((r) => ({ tripId: r.eapi_trip_id as string, legId: r.leg_id as string }));
    },
  });
  // Legs that own a live external trip. The equipment-removal gate is per
  // leg: a trip on leg 1 must never block clearing a truck on leg 2.
  const liveTripLegIds = new Set((activeLegTripsForOrder ?? []).map((t) => t.legId));
  // Trip ids live only on legs + trips — never fall back to the deprecated
  // `orders.eapi_trip_id` column (AOP-WENP-03315).
  const hasActiveExternalTrip = (activeLegTripsForOrder?.length ?? 0) > 0;
  // Eligibility for the historical/billing-only edit toggle. Limited to
  // admins editing a delivered or canceled order — the two statuses where
  // the "fix billing without touching the external trip" workflow makes
  // sense. `historicalEdit` is the *effective* flag (eligibility + user
  // toggle) and is what every gate below references.
  const orderStatusNormalized = order ? normalizeOrderStatus(order.status) : "booked";
  const historicalEditEligible =
    !!order &&
    isAdmin &&
    (orderStatusNormalized === "delivered" || orderStatusNormalized === "canceled");
  const historicalEdit = historicalEditEligible && historicalEditUserToggle;
  /** Best-effort: representative active EAPI trip id for change_events rows. */
  const representativeEapiTripId = activeLegTripsForOrder?.[0]?.tripId ?? null;

  // Resolve trailer ID against the checked-in roster so we can drive the Trailer Type field.
  // (Hooks must be called at the top level — before the early `if (!order) return null;`.)
  const {
    matched,
    matchedTrailerRowId,
    inheritedTypeId,
    inheritedTypeLabel,
    isLoading: checkedInTrailersLoading,
  } = useResolvedCheckedInTrailer(
    formData.trailer_id,
  );
  // Trailer type is now always required when a trailer is set — the inherited
  // value from the check-in record still pre-fills the select, but the user
  // may override it (gated by a confirmation dialog).
  const trailerTypeRequired = !!formData.trailer_id.trim();

  // Whether this order's order-level origin is a YMS-tracked yard. The
  // trailer-on-site confirmation gate only applies when the origin
  // (`ship_from_location_id`) is YMS-tracked — otherwise there's no yard
  // for the trailer to check into. This mirrors the server-side gate in
  // `useOrderMutations` which also keys off the order-level origin only.
  const originLocationId =
    typeof formData.ship_from_location_id === "string" && formData.ship_from_location_id.trim()
      ? formData.ship_from_location_id
      : null;

  const {
    data: ymsCheck,
    isFetching: ymsCheckFetching,
    isSuccess: ymsCheckSuccess,
    isError: ymsCheckErrored,
  } = useQuery({
    queryKey: ["order-origin-track-in-yms", originLocationId],
    enabled: !!originLocationId,
    queryFn: async () => {
      const sb = supabase as any;
      const { data, error } = await sb
        .from("locations")
        .select("track_in_yms")
        .eq("id", originLocationId)
        .maybeSingle();
      if (error) throw error;
      // Fail closed when the row is missing, matching the mutation's posture.
      return data == null ? true : !!(data as { track_in_yms: boolean | null }).track_in_yms;
    },
  });
  // Fail closed on a failed lookup too: an errored check is "answered
  // strictly", never "still pending" — otherwise quick-confirm waits forever.
  const requiresOnSite = ymsCheckErrored ? true : !!ymsCheck;
  // True when we need the YMS check to resolve before we can safely
  // auto-confirm (i.e. there's an origin location and the query hasn't
  // settled yet — success or failure both count as settled).
  const ymsCheckReady =
    !originLocationId || ((ymsCheckSuccess || ymsCheckErrored) && !ymsCheckFetching);


  // Override flow: lets the user change the trailer type even when a
  // checked-in trailer supplied it. The new value is staged here and the
  // matched `trailers` row is updated on submit.
  const [pendingTypeChange, setPendingTypeChange] = useState<string | null>(null);
  const [trailerTypeOverride, setTrailerTypeOverride] = useState<{
    trailerRowId: string;
    newTypeId: string;
  } | null>(null);

  // Drop any pending override when the matched trailer changes so we never
  // write to the wrong row.
  useEffect(() => {
    setTrailerTypeOverride((prev) => {
      if (!prev) return prev;
      if (!matchedTrailerRowId || prev.trailerRowId !== matchedTrailerRowId) return null;
      return prev;
    });
    setPendingTypeChange(null);
  }, [matchedTrailerRowId]);

  useSyncInheritedTrailerType(
    inheritedTypeId,
    matched,
    formData.trailer_type_id,
    useCallback((id: string) => setFormData((prev) => ({ ...prev, trailer_type_id: id })), []),
    !!trailerTypeOverride,
  );

  // Custom trailer (typed ID with no checked-in match) on an order departing
  // an origin with a known default type: pre-fill that type once per newly
  // entered trailer ID, and remind the user to verify it.
  const originShortCode = locations?.find((l) => l.id === originLocationId)?.short_code ?? null;
  const customTrailerDefaultSlug = customTrailerDefaultSlugFor(originShortCode);
  const customTrailerDefaultTypeId =
    (customTrailerDefaultSlug && trailerTypes?.find((t) => t.slug === customTrailerDefaultSlug)?.id) || null;
  const trimmedTrailerId = formData.trailer_id.trim();
  // A "custom" trailer is one the user typed that isn't on the checked-in
  // roster (checked-in trailers inherit their type instead). Only a trailer
  // that differs from the saved one counts, so opening an existing order
  // never rewrites its stored type, and we wait for the roster to load so a
  // checked-in trailer doesn't briefly look custom and flash the default.
  const isNewCustomTrailer =
    !!trimmedTrailerId &&
    trimmedTrailerId !== (order?.trailer_id || "").trim() &&
    !matched &&
    !checkedInTrailersLoading;
  const lastDefaultedTrailerIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isNewCustomTrailer || !customTrailerDefaultTypeId) return;
    if (lastDefaultedTrailerIdRef.current === trimmedTrailerId) return;
    lastDefaultedTrailerIdRef.current = trimmedTrailerId;
    setFormData((prev) => ({ ...prev, trailer_type_id: customTrailerDefaultTypeId }));
  }, [isNewCustomTrailer, customTrailerDefaultTypeId, trimmedTrailerId]);
  const showCustomTrailerTypeReminder =
    isNewCustomTrailer &&
    !!customTrailerDefaultTypeId &&
    formData.trailer_type_id === customTrailerDefaultTypeId;

  // Legs come from public.legs — the canonical (and only) storage for stops.
  const { data: childLegs } = useQuery({
    queryKey: ["order-child-legs", order?.id],
    queryFn: async () => {
      const { data: legRows, error: legsErr } = await supabase
        .from("legs")
        .select("*")
        .eq("order_id", order!.id)
        .order("leg_sequence", { ascending: true });
      if (legsErr) throw legsErr;
      if (!legRows || legRows.length === 0) return [] as any[];
      const legIds = legRows.map((r: any) => r.id);
      const { data: tripRows } = await supabase
        .from("trips")
        .select("leg_id, eapi_trip_id, is_active")
        .in("leg_id", legIds)
        .eq("is_active", true);
      const tripByLegId = new Map(
        (tripRows ?? []).map((t: any) => [t.leg_id, t.eapi_trip_id]),
      );
      return legRows.map((r) => ({
        ...(r as any),
        eapi_trip_id: tripByLegId.get((r as any).id) ?? null,
        kind: "leg" as const,
      })) as any[];
    },
    enabled: !!order?.id && isMultiLeg && open,
  });

  // Single-leg orders keep the advanced equipment columns (is_loaded,
  // is_temperature_controlled, temperature_f) on the mirrored `legs` row —
  // `public.orders` has no counterpart — so hydrate them from there.
  const { data: singleLegRow, isFetched: singleLegFetched } = useQuery({
    queryKey: ["order-single-leg-advanced", order?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legs")
        .select("*")
        .eq("order_id", order!.id)
        .order("leg_sequence", { ascending: true })
        .limit(1);
      if (error) throw error;
      return (data?.[0] as any) ?? null;
    },
    enabled: !!order?.id && !isMultiLeg && open,
  });

  const findMatchingLocationId = (streetAddress: string, city: string): string => {
    if (!locations) return "";
    const match = locations.find((l) => l.street_address === streetAddress && l.city === city);
    return match?.id || "";
  };

  /** Returns the "YYYY-MM-DDTHH:mm" Central wall-clock the form shows. The
   *  edit form is always in Central, whatever the stop or viewer timezone. */
  const toLocalForStop = (utc: string | null | undefined, _street?: string, _city?: string): string =>
    utcToFormLocal(utc);

  // Initialize form when order changes
  useEffect(() => {
    if (order && open) {
      if (isMultiLeg && childLegs) {
        const legs: LegData[] = childLegs.map((child: any) => ({
          id: child.id,
          kind: child.kind ?? "leg",
          driver_status: (child as any).driver_status ?? null,
          ship_from_location_id:
            (child.ship_from_location_id as string | null | undefined) ||
            findMatchingLocationId(child.ship_from_street_address, child.ship_from_city),
          ship_from_street_address: child.ship_from_street_address,
          ship_from_city: child.ship_from_city,
          ship_from_province_state: child.ship_from_province_state || "",
          ship_from_postal_zip_code: child.ship_from_postal_zip_code || "",
          ship_from_country: child.ship_from_country || "",
          ship_to_location_id:
            (child.ship_to_location_id as string | null | undefined) ||
            findMatchingLocationId(child.ship_to_street_address, child.ship_to_city),
          ship_to_street_address: child.ship_to_street_address,
          ship_to_city: child.ship_to_city,
          ship_to_province_state: child.ship_to_province_state || "",
          ship_to_postal_zip_code: child.ship_to_postal_zip_code || "",
          ship_to_country: child.ship_to_country || "",
          ready_time: toLocalForStop(child.ready_time, child.ship_from_street_address, child.ship_from_city),
          requested_delivery_time: toLocalForStop(child.requested_delivery_time, child.ship_to_street_address, child.ship_to_city),
          ready_time_end: toLocalForStop(
            (child as any).ready_time_end ?? child.ready_time,
            child.ship_from_street_address, child.ship_from_city,
          ),
          requested_delivery_time_end: toLocalForStop(
            (child as any).requested_delivery_time_end ?? child.requested_delivery_time,
            child.ship_to_street_address, child.ship_to_city,
          ),
          ...hydrateLegAdvanced(child),
          // Per-leg asset assignments (Phase 4 plumbing). Loaded from the
          // legacy child orders rows during the dual-write window.
          assigned_tractor_id: (child as any).assigned_tractor_id || "",
          assigned_operator_id: (child as any).assigned_operator_id || "",
          assigned_operator_2_id: (child as any).assigned_operator_2_id || "",
          trailer_type_id: (child as any).trailer_type_id || "",
          // Per-leg trailer_id hydration. `legs.trailer_id` is the source
          // of truth (PER_LEG_TRAILER_MIGRATION) — never inject the parent
          // order's stale leg-1 mirror when a leg is genuinely empty, or
          // the edit dialog silently repopulates cleared trailers.
          trailer_id: (child as any).trailer_id || "",
          trip_type: (child as any).trip_type || "",
        }));
        // Detect whether every leg already shares the same assets. If so,
        // start the toggle ON (today's UX). Otherwise start OFF so the user
        // can see the mixed assignments per leg.
        const allSameAssets =
          legs.length === 0 ||
          legs.every(
            (l) =>
              (l.assigned_tractor_id || "") === (legs[0].assigned_tractor_id || "") &&
              (l.assigned_operator_id || "") === (legs[0].assigned_operator_id || "") &&
              (l.assigned_operator_2_id || "") === (legs[0].assigned_operator_2_id || "") &&
              (l.trailer_type_id || "") === (legs[0].trailer_type_id || "") &&
              (l.trailer_id || "") === (legs[0].trailer_id || ""),
          );
        // Same logic for trip type: if any leg specifies a trip_type that
        // differs from the parent's, start the per-leg trip-type toggle OFF
        // so the user sees the divergence. Null/empty leg values are treated
        // as inheriting from the parent.
        const parentTripType = (order as any).trip_type || "";
        const allSameTripType =
          legs.length === 0 ||
          legs.every((l) => {
            const lt = l.trip_type || "";
            return lt === "" || lt === parentTripType;
          });
        const multiLegData: OrderFormData = {
          order_type: "multi-leg",
          legs,
          status: normalizeOrderStatus(order.status),
          ship_from_location_id: "",
          ship_from_street_address: order.ship_from_street_address,
          ship_from_city: order.ship_from_city,
          ship_from_province_state: order.ship_from_province_state || "",
          ship_from_postal_zip_code: order.ship_from_postal_zip_code || "",
          ship_from_country: order.ship_from_country || "",
          ship_to_location_id: "",
          ship_to_street_address: order.ship_to_street_address,
          ship_to_city: order.ship_to_city,
          ship_to_province_state: order.ship_to_province_state || "",
          ship_to_postal_zip_code: order.ship_to_postal_zip_code || "",
          ship_to_country: order.ship_to_country || "",
          ready_time: toLocalForStop(order.ready_time, order.ship_from_street_address, order.ship_from_city),
          requested_delivery_time: toLocalForStop(order.requested_delivery_time, order.ship_to_street_address, order.ship_to_city),
          ready_time_end: toLocalForStop(
            (order as any).ready_time_end ?? order.ready_time,
            order.ship_from_street_address, order.ship_from_city,
          ),
          requested_delivery_time_end: toLocalForStop(
            (order as any).requested_delivery_time_end ?? order.requested_delivery_time,
            order.ship_to_street_address, order.ship_to_city,
          ),
          billing_party_id: order.billing_party_id,
          trailer_id: order.trailer_id || "",
          trailer_type_id: order.trailer_type_id || "",
          tender_id: order.tender_id || "",
          pending_bol: (order as any).pending_bol === true,
          assigned_tractor_id: order.assigned_tractor_id || "",
          assigned_operator_id: order.assigned_operator_id || "",
          assigned_operator_2_id: order.assigned_operator_2_id || "",
          trip_type: (order as any).trip_type || "",
          is_recurring: order.is_recurring || false,
          recurrence_days: order.recurrence_days || [],
          recurrence_end_date: order.recurrence_end_date || "",
          use_same_assets_for_all_legs: allSameAssets,
          use_same_trip_type_for_all_legs: allSameTripType,
          ...hydrateOrderAdvanced(order as any),
        };
        multiLegData.show_advanced_fields = hasAnyAdvancedValues(multiLegData);
        setFormData(multiLegData);
        initialFormDataRef.current = JSON.stringify(multiLegData);
      } else if (!isMultiLeg) {
        // Wait for the mirrored leg row so equipment fields hydrate correctly.
        if (!singleLegFetched) return;
        const singleLegData: OrderFormData = {
          order_type: "single-leg",
          legs: [],
          status: normalizeOrderStatus(order.status),
          ship_from_location_id:
            (order as any).ship_from_location_id ||
            findMatchingLocationId(order.ship_from_street_address, order.ship_from_city),
          ship_from_street_address: order.ship_from_street_address,
          ship_from_city: order.ship_from_city,
          ship_from_province_state: order.ship_from_province_state || "",
          ship_from_postal_zip_code: order.ship_from_postal_zip_code || "",
          ship_from_country: order.ship_from_country || "",
          ship_to_location_id:
            (order as any).ship_to_location_id ||
            findMatchingLocationId(order.ship_to_street_address, order.ship_to_city),
          ship_to_street_address: order.ship_to_street_address,
          ship_to_city: order.ship_to_city,
          ship_to_province_state: order.ship_to_province_state || "",
          ship_to_postal_zip_code: order.ship_to_postal_zip_code || "",
          ship_to_country: order.ship_to_country || "",
          ready_time: toLocalForStop(order.ready_time, order.ship_from_street_address, order.ship_from_city),
          requested_delivery_time: toLocalForStop(order.requested_delivery_time, order.ship_to_street_address, order.ship_to_city),
          ready_time_end: toLocalForStop(
            (order as any).ready_time_end ?? order.ready_time,
            order.ship_from_street_address, order.ship_from_city,
          ),
          requested_delivery_time_end: toLocalForStop(
            (order as any).requested_delivery_time_end ?? order.requested_delivery_time,
            order.ship_to_street_address, order.ship_to_city,
          ),
          billing_party_id: order.billing_party_id,
          trailer_id: order.trailer_id || "",
          trailer_type_id: order.trailer_type_id || "",
          tender_id: order.tender_id || "",
          pending_bol: (order as any).pending_bol === true,
          assigned_tractor_id: order.assigned_tractor_id || "",
          assigned_operator_id: order.assigned_operator_id || "",
          assigned_operator_2_id: order.assigned_operator_2_id || "",
          trip_type: (order as any).trip_type || "",
          is_recurring: order.is_recurring || false,
          recurrence_days: order.recurrence_days || [],
          recurrence_end_date: order.recurrence_end_date || "",
          ...hydrateOrderAdvanced(order as any),
          ...hydrateLegAdvanced(singleLegRow ?? (order as any)),
        };
        singleLegData.show_advanced_fields = hasAnyAdvancedValues(singleLegData);
        setFormData(singleLegData);
        initialFormDataRef.current = JSON.stringify(singleLegData);
      }
    }
  }, [order, open, locations, childLegs, isMultiLeg, singleLegRow, singleLegFetched]);

  const { updateMutation } = useOrderMutations({
    userEmail: user?.email,
    tractors,
    drivers,
    locations,
    onSuccess: () => {
      onOpenChange(false);
      onSuccess?.();
    },
  });

  const hasChanges = useMemo(() => {
    if (!initialFormDataRef.current) return false;
    return JSON.stringify(formData) !== initialFormDataRef.current;
  }, [formData]);

  // Confirmation no longer runs through this form. The order page confirms
  // straight from the stored order + legs (`useConfirmOrder`); this dialog is
  // for editing only.
  useEffect(() => {
    if (open) return;
    setPendingTypeChange(null);
    setTrailerTypeOverride(null);
  }, [open]);


  const handleTractorChange = (value: string) => {
    const newTractorId = value === "none" ? "" : value;
    const originalTractorId = order?.assigned_tractor_id || "";
    // If order has an external trip and tractor is actually changing, show
    // warning. Historical/billing-only edits never touch EAPI, so suppress.
    if (!historicalEdit && hasActiveExternalTrip && newTractorId !== originalTractorId) {
      setPendingTractorId(newTractorId);
      setShowTractorWarning(true);
    } else {
      setFormData((prev) => ({ ...prev, assigned_tractor_id: newTractorId }));
    }
  };

  const handleTractorWarningConfirm = () => {
    if (pendingTractorId !== null) {
      setFormData((prev) => ({ ...prev, assigned_tractor_id: pendingTractorId }));
    }
    setPendingPivotReason(resolvedPivotReason || null);
    setShowTractorWarning(false);
    setPendingTractorId(null);
    resetPivotReason();
  };

  const handleTractorWarningCancel = () => {
    setShowTractorWarning(false);
    setPendingTractorId(null);
    resetPivotReason();
  };

  const handleTrailerChange = (value: string) => {
    const newTrailerId = value;
    const originalTrailerId = order?.trailer_id || "";
    // Needs-Service guard now lives inside `TrailerIdField` — by the time we
    // reach this callback the trailer is known-assignable. We still need to
    // gate on active external trips (the EAPI-pivot warning) for changes.
    if (
      !historicalEdit &&
      hasActiveExternalTrip &&
      newTrailerId.trim() !== originalTrailerId.trim()
    ) {
      setPendingTrailerId(newTrailerId);
      setShowTrailerWarning(true);
    } else {
      setFormData((prev) => ({ ...prev, trailer_id: newTrailerId }));
    }
  };

  const handleTrailerWarningConfirm = () => {
    if (pendingTrailerId !== null) {
      setFormData((prev) => ({ ...prev, trailer_id: pendingTrailerId }));
    }
    setPendingPivotReason(resolvedPivotReason || null);
    setShowTrailerWarning(false);
    setPendingTrailerId(null);
    resetPivotReason();
  };

  const handleTrailerWarningCancel = () => {
    setShowTrailerWarning(false);
    setPendingTrailerId(null);
    resetPivotReason();
  };

  /** Intercepts trip_type / trailer_type_id changes that would switch an EAPI-tracked order to a no-EAPI state. */
  const handleTripTypeChange = (value: string) => {
    // No inline confirmation dialog — the post-Save ChangeReasonDialog
    // captures the pivot-to-Manual reason and drives the upstream
    // `/abandon` payload. See handleSubmit pivotInputs block.
    setFormData((prev) => ({ ...prev, trip_type: value }));
  };

  // Apply a trailer-type value, routing through the existing EAPI-skip
  // intercept if it would change EAPI eligibility for a tracked order.
  const applyTrailerTypeValue = (newVal: string) => {
    setFormData((prev) => ({ ...prev, trailer_type_id: newVal }));
  };

  const handleTrailerTypeChange = (value: string) => {
    const newVal = value === "none" ? "" : value;
    // If the user is overriding a value inherited from a checked-in trailer,
    // confirm before accepting the change (the trailers row will be updated
    // on submit).
    if (matched && matchedTrailerRowId && newVal !== (inheritedTypeId ?? "")) {
      setPendingTypeChange(newVal);
      return;
    }
    applyTrailerTypeValue(newVal);
  };

  const confirmTrailerTypeOverride = () => {
    if (pendingTypeChange === null || !matchedTrailerRowId) {
      setPendingTypeChange(null);
      return;
    }
    const newTypeId = pendingTypeChange;
    setTrailerTypeOverride({ trailerRowId: matchedTrailerRowId, newTypeId });
    setPendingTypeChange(null);
    applyTrailerTypeValue(newTypeId);
  };

  const cancelTrailerTypeOverride = () => setPendingTypeChange(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!order) return;

    // A delivered ORDER is not the same as a delivered STOP. On a multi-leg
    // order the parent status is a roll-up, so freezing the whole form off it
    // locked stops that had never started (AOP-HTSH-06110). Only fall back to
    // the tender-only save when every stop is itself finished; otherwise the
    // per-leg guard below decides, stop by stop.
    const everyStopFinished = (() => {
      const locks = resolveLegEditLocks(formData, { orderStatus: order.status });
      if (locks.length === 0) return true;
      return locks.every((l) => l.assetsLocked && l.timesLocked && l.locationsLocked);
    })();

    if (normalizeOrderStatus(order.status) === "delivered" && !isAdmin && everyStopFinished) {
      toast.info("This order is delivered — only the Tender ID can be changed.");
      const originalData = initialFormDataRef.current
        ? (JSON.parse(initialFormDataRef.current) as OrderFormData)
        : formData;
      updateMutation.mutate({
        id: order.id,
        data: { ...originalData, tender_id: formData.tender_id },
        originalOrder: order,
      });
      setPendingPivotReason(null);
      return;
    }

    // Trailer Type guard (full mode only — assign modes don't touch this field).
    if (isFullMode && trailerTypeRequired && !formData.trailer_type_id) {
      toast.error("Trailer Type is required when the trailer is not checked in");
      return;
    }
    // Per-leg HOS / chronology / asset-conflict checks (Phase 4). Multi-leg
    // orders only — single-leg orders are governed by the order-level fields.
    if (isFullMode) {
      // Per-leg lock guard. Only a stop that is itself delivered / canceled /
      // in transit rejects its own changes — sibling stops stay editable.
      const original = initialFormDataRef.current
        ? (JSON.parse(initialFormDataRef.current) as OrderFormData)
        : undefined;
      const lockErrors = validateLegEdits(formData, original, {
        orderStatus: order.status,
        historicalEdit,
      });
      if (lockErrors.length > 0) {
        toast.error(lockErrors[0]);
        return;
      }
      const legErrors = validateLegs(formData);
      if (legErrors.length > 0) {
        toast.error(legErrors[0]);
        return;
      }
      const advancedErrors = validateAdvancedFields(formData);
      if (advancedErrors.length > 0) {
        toast.error(advancedErrors[0]);
        return;
      }
    }
    // Truck eligibility hard block (mirrors the picker gating).
    const eligibilityError = validateTractorEligibility(
      buildEligibilityChecks(formData, tractors, { orderStatus: order.status }),
      eligibilityByTractor,
      eligibilityGateState,
    );
    if (eligibilityError) {
      toast.error(eligibilityError);
      return;
    }
    // Out-of-service hard block — a truck assigned earlier can go OOS while
    // the dialog is open, so re-run the picker's rule against the same
    // per-leg windows before saving.
    const oosSaveError = validateTractorOosAssignments(
      buildOosAssignmentChecks(formData, tractors),
      oosBlockedByLeg,
    );
    if (oosSaveError) {
      toast.error(oosSaveError);
      return;
    }
    // ── Confirmation gates ──
    // 1. Promotion to confirmed requires both truck and trailer. Confirmation
    //    is an explicit manual action by dispatch — adding a trailer alone
    //    never promotes the order. Multi-leg with per-leg assets is checked
    //    per leg by `hasConfirmAssets`; otherwise the parent mirrors are used.
    const assets = hasConfirmAssets(formData, trailerTypes);
    // Only a *promotion* to confirmed needs the full asset set. Re-saving an
    // order that is already confirmed must not be re-gated on assets it was
    // allowed to be saved without.
    const wantsConfirm = formData.status === "confirmed" && orderStatusNormalized !== "confirmed";
    if (wantsConfirm && !assets.ok) {
      setShowConfirmRequiresWarning(true);
      return;
    }
    // Document Confirm Gating no longer lives here: promotion to confirmed is
    // an explicit order-page action (`useConfirmOrder`), which owns the
    // documentation gate. The edit form only saves order details.
    // 2. An order with a live external trip cannot have its truck/trailer
    // *removed*. This is a diff — an order that never had a trailer stays
    // saveable, so assigning a truck to it is not treated as a removal.
    // Historical/billing edits skip this — they intentionally don't sync
    // to the external trip, so the trip-integrity gate is moot.
    const legRows = ((childLegs as any[]) ?? []) as Array<{ id?: string; leg_sequence?: number }>;
    const leg1Row = isMultiLeg
      ? legRows.find((l) => l?.leg_sequence === 1) ?? legRows[0]
      : (singleLegRow as any);
    const removesAssets = removesConfirmedAssets({
      perLeg: assets.perLeg,
      liveTrip: {
        // The parent columns mirror leg 1, so they are gated by leg 1's trip.
        parent: isMultiLeg
          ? !!leg1Row?.id && liveTripLegIds.has(leg1Row.id)
          : hasActiveExternalTrip,
        legs: legRows.map((l) => !!l?.id && liveTripLegIds.has(l.id)),
      },
      before: {
        parent: {
          tractorId: order?.assigned_tractor_id ?? null,
          trailerId: order?.trailer_id ?? null,
        },
        legs: ((childLegs as any[]) ?? []).map((l) => ({
          tractorId: l?.assigned_tractor_id ?? null,
          trailerId: l?.trailer_id ?? null,
        })),
      },
      after: {
        parent: {
          tractorId: formData.assigned_tractor_id,
          trailerId: formData.trailer_id,
        },
        legs: (formData.legs ?? []).map((l) => ({
          tractorId: l.assigned_tractor_id,
          trailerId: l.trailer_id,
        })),
      },
    });
    if (
      !historicalEdit &&
      hasActiveExternalTrip &&
      formData.status !== "canceled" &&
      removesAssets
    ) {
      setShowCannotRemoveWarning(true);
      return;
    }
    // Cross-tenant detection: if the trailer_id is changing/being added and
    // the trailer doesn't exist in this tenant, see if it lives elsewhere.
    const parentTrailerSet = !!formData.trailer_id?.trim();
    if (
      !skipCrossTenantOnce &&
      tenantId &&
      parentTrailerSet &&
      formData.trailer_id.trim() !== (order.trailer_id || "").trim()
    ) {
      const next = formData.trailer_id.trim();
      // Needs-Service safety net for typed-in (non-combobox) trailers.
      const assign = await assertTrailerAssignable(next, tenantId, queryClient);
      if (assign.ok === false) {
        setNeedsServiceBlock({ trailerId: assign.trailerId, reason: assign.serviceReason });
        return;
      }
      const { data: existsHere } = await supabase
        .from("trailers")
        .select("id")
        .eq("trailer_id", next)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (!existsHere) {
        const matches = await findTrailerInOtherTenants(next, tenantId);
        if (matches.length > 0) {
          setCrossTenantMatches(matches);
          setShowCrossTenantDialog(true);
          return;
        }
      }
    }
    if (skipCrossTenantOnce) setSkipCrossTenantOnce(false);
    // Safety net: when a checked-in trailer supplies a type AND the user has
    // not explicitly overridden it, ensure the payload reflects the inherited
    // value even if the sync effect hasn't flushed yet.
    const payload =
      isFullMode &&
      !trailerTypeOverride &&
      matched &&
      inheritedTypeId &&
      formData.trailer_type_id !== inheritedTypeId
        ? { ...formData, trailer_type_id: inheritedTypeId }
        : formData;
    // Propagate a confirmed override to the matched trailers row before the
    // order mutation fires. Abort the submit on failure so the order doesn't
    // diverge from the check-in record.
    if (trailerTypeOverride && tenantId) {
      const { error: trailerErr } = await supabase
        .from("trailers")
        .update({ trailer_type_id: trailerTypeOverride.newTypeId })
        .eq("id", trailerTypeOverride.trailerRowId)
        .eq("tenant_id", tenantId);
      if (trailerErr) {
        toast.error(`Failed to update check-in record: ${trailerErr.message}`);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["checked-in-trailers", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["trailers"] });
      setTrailerTypeOverride(null);
    }
    // Stamp the historical-edit flag onto the payload so the mutation hook
    // short-circuits every EAPI side effect. Read by `useOrderMutations`.
    // `_pivotReason` is filled in below from the ChangeReasonDialog result
    // (when a trip_type pivot is detected) so the upstream `/abandon` call
    // forwards the dispatcher's selected reason instead of a hardcoded
    // fallback.
    const payloadWithPivot = pendingPivotReason
      ? ({ ...payload, _pivotReason: pendingPivotReason } as typeof payload)
      : payload;
    let payloadFinal: any = historicalEdit
      ? ({ ...payloadWithPivot, _historicalEdit: true })
      : payloadWithPivot;
    if (historicalEdit) {
      console.info(
        "[OrderEditDialog] historical/billing edit — skipping all EAPI sync",
        { orderId: order.id, uniqueId: order.unique_id, status: order.status },
      );
    }

    // ── Trip pivot reason capture (trip_type / trailer / tractor) ──
    // Skip when status is being moved to canceled — the cancel dialog already
    // owns reason capture for that path.
    // Skip entirely in historical mode — these aren't pivots, they're
    // backdated corrections.
    let pivotInputs: ReturnType<typeof buildPivotInputs> = [];
    if (
      !historicalEdit &&
      formData.status !== "canceled" &&
      (order as any).tenant_id
    ) {
      const orig = {
        trip_type: (order as any).trip_type ?? null,
        trailer_id: order.trailer_id ?? null,
        assigned_tractor_id: order.assigned_tractor_id ?? null,
      };
      const nxt = {
        trip_type: payloadFinal.trip_type ?? null,
        trailer_id: payloadFinal.trailer_id ?? null,
        assigned_tractor_id: payloadFinal.assigned_tractor_id ?? null,
      };
      const prompts = detectPivotPrompts(orig, nxt, resolvePivotLabel);
      if (prompts.length > 0) {
        const results = await promptPivotReasons(prompts, {
          orderUniqueId: order.unique_id,
        });
        if (!results) return; // user dismissed → abort save
        pivotInputs = buildPivotInputs(orig, nxt, results, {
          orderId: order.id,
          tenantId: (order as any).tenant_id,
              eapiTripId: representativeEapiTripId,
        });
        // Forward the trip_type pivot reason into the EAPI `/abandon`
        // payload (used in useOrderMutations no-EAPI-switch branch). Falls
        // back to "Pivoted to manual" in the mutation if absent.
        const tripTypeResult = results.find((r) => r.key === "trip_type");
        if (tripTypeResult) {
          const reasonText =
            tripTypeResult.reason === "other"
              ? (tripTypeResult.otherText ?? "").trim() || "Pivoted to manual"
              : REASON_LABELS[tripTypeResult.reason];
          payloadFinal = { ...payloadFinal, _pivotReason: reasonText };
        }
      }

      // ── Per-leg pivot prompts (multi-leg, per-leg toggles) ──
      // When EITHER "use same assets" or "use same trip type" is toggled
      // off, the order-level diff above misses per-leg changes (the parent
      // fields stay empty / null). Loop the legs and prompt per leg so the
      // audit trail and downstream EAPI abandon/cancel logic both fire.
      // For fields that ARE still shared, the diff is nulled on both sides,
      // so `detectPivotPrompts` returns nothing and the loop is a no-op.
      if (
        isMultiLeg &&
        payloadFinal.order_type === "multi-leg" &&
        (!payloadFinal.use_same_assets_for_all_legs ||
          !payloadFinal.use_same_trip_type_for_all_legs) &&
        Array.isArray(childLegs)
      ) {
        const useSameAssets = !!payloadFinal.use_same_assets_for_all_legs;
        const useSameTripType = !!payloadFinal.use_same_trip_type_for_all_legs;
        const snapById = new Map<string, any>(
          childLegs.map((c: any) => [c.id, c]),
        );
        for (let i = 0; i < payloadFinal.legs.length; i++) {
          const leg = payloadFinal.legs[i];
          if (!leg.id) continue;
          const snap = snapById.get(leg.id);
          if (!snap) continue;
          const legOrig = {
            // Null the fields that are still SHARED — they can only change
            // via the parent diff, not per-leg, and including them here
            // would produce false prompts on save.
            // When the per-leg override was never set (null), the leg was
            // inheriting the parent's value — so diff against the parent's
            // current value, not empty. Otherwise `null → "Manual"` fails
            // the both-non-empty guard in `detectPivotPrompts` and the
            // reason prompt is silently skipped.
            trip_type: useSameTripType
              ? null
              : (snap.trip_type ?? (order as any).trip_type ?? null),
            // `legs.trailer_id` is authoritative — do NOT fall back to
            // `order.trailer_id` (a leg-1 mirror that goes stale for legs
            // 2..N and inflates the diff when hydrating cleared values).
            trailer_id: useSameAssets
              ? null
              : ((snap.trailer_id as string | null | undefined) ?? null),
            // `legs.assigned_tractor_id` is authoritative — same rule as
            // trailer. Falling back to `order.assigned_tractor_id` (a leg-1
            // mirror) made assigning a truck to an empty leg 2 look like a
            // pivot away from leg 1's truck, prompting "Change from <leg 1
            // truck> to <new truck>".
            assigned_tractor_id: useSameAssets
              ? null
              : (snap.assigned_tractor_id ?? null),
          };
          const legNxt = {
            trip_type: useSameTripType ? null : (leg.trip_type ?? null),
            trailer_id: useSameAssets
              ? null
              : ((leg.trailer_id ?? null) as string | null),
            assigned_tractor_id: useSameAssets
              ? null
              : (leg.assigned_tractor_id ?? null),
          };
          // Diff all three per-leg fields: trip_type, tractor, AND trailer.
          // Trailer used to be order-level only, but Phase 1/2 introduced
          // per-leg trailer_id — the order-level diff above sees empty →
          // empty in per-leg-assets mode, so we must diff it here.
          const legPrompts = detectPivotPrompts(legOrig, legNxt, resolvePivotLabel);
          if (legPrompts.length === 0) continue;
          const results = await promptPivotReasons(legPrompts, {
            orderUniqueId: `${order.unique_id} · Leg ${i + 1}`,
          });
          if (!results) return; // user dismissed → abort save
          // Reuse the representative leg trip id when present so the audit
          // row joins back to a real EAPI trip.
          const legTripId =
            (snap.eapi_trip_id as string | null | undefined) ??
            representativeEapiTripId;
          pivotInputs = pivotInputs.concat(
            buildPivotInputs(
              { ...legOrig, trailer_id: legNxt.trailer_id },
              legNxt,
              results,
              {
                orderId: order.id,
                tenantId: (order as any).tenant_id,
                eapiTripId: legTripId,
              },
            ),
          );
        }
      }
    }

    updateMutation.mutate(
      { id: order.id, data: payloadFinal, originalOrder: order },
      {
        onSuccess: () => {
          // Pivot reason codes (trip_type / trailer / tractor). Cancellation
          // reasons are no longer captured here — cancelling is an order-page
          // action (`useOrderCancellation`), not a form save.
          const allInputs = pivotInputs;
          if (allInputs.length > 0) {
            void recordChangeEvents(allInputs).then((result) => {
              if (result.error) {
                console.warn(
                  "[OrderEditDialog] change_events insert failed",
                  result.error,
                );
                toast.warning(
                  `Order ${order.unique_id} updated, but reason code(s) were not recorded: ${result.error}. Please re-enter via order history.`,
                  { duration: 8000 },
                );
              }
            }).catch((err) => {
              console.warn("[OrderEditDialog] change_events insert threw", err);
              toast.warning(
                `Order ${order.unique_id} updated, but reason code(s) were not recorded: ${err?.message ?? err}. Please re-enter via order history.`,
                { duration: 8000 },
              );
            });
          }
        },
      },
    );
    setPendingPivotReason(null);
  };

  if (!order) return null;

  const isFullMode = mode === "full";
  const isAssignDriverMode = mode === "assign-driver";
  const isAssignTractorMode = mode === "assign-tractor";
  const isAssignMode = isAssignDriverMode || isAssignTractorMode;
  const normalizedOrderStatus = normalizeOrderStatus(order.status);
  const normalizedFormStatus = normalizeOrderStatus(formData.status);
  const isDelivered = normalizedOrderStatus === "delivered" || normalizedFormStatus === "delivered";
  // Admins can edit delivered orders to correct dispatch mistakes; everyone
  // else still sees the field-level lockdown.
  const deliveredLocked = isDelivered && !isAdmin;
  const deliveredLockTitle = "Contact an administrator to adjust delivered order fields";
  const displayStatus = isDelivered ? "delivered" : normalizedFormStatus;

  const getDialogTitle = () => {
    if (isAssignDriverMode) return `Assign Driver - ${order.unique_id}`;
    if (isAssignTractorMode) return `Assign Tractor - ${order.unique_id}`;
    return `Edit Order ${order.unique_id}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isAssignMode ? "max-w-md" : "max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"}>
        <div className={isFullMode ? "px-6 pt-6 pb-2" : ""}>
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
            <DialogTitle>
              {getDialogTitle()}
              {isFullMode && recurringTemplate?.unique_id && order.recurring_order_template_id && (
                <>{" "}
                  <Link to={`/templates/${order.recurring_order_template_id}`} onClick={() => onOpenChange(false)}>
                    <Badge variant="outline" className="gap-1 text-xs font-normal hover:bg-accent cursor-pointer align-middle">
                      <Repeat className="h-3 w-3" />
                      {recurringTemplate.unique_id}
                    </Badge>
                  </Link>
                </>
              )}
            </DialogTitle>
            {isFullMode && (
              <AdvancedFieldsToggle
                id="edit-show-advanced-fields"
                checked={formData.show_advanced_fields === true}
                onCheckedChange={(next) =>
                  setFormData((prev) => ({ ...prev, show_advanced_fields: next }))
                }
              />
            )}
            </div>
          </DialogHeader>
        </div>

        {(() => {
          // Single source of truth: hide the "No External Trip ID" banner
          // when ANY active external trip is attached (parent or any leg).
          // Also hide the EAPI banner in historical/billing mode — it's
          // noise when external sync is intentionally suppressed.
          const showEapiBanner = !!order && !hasActiveExternalTrip && !historicalEdit;
          const showDeliveredBanner = isFullMode && isDelivered;
          const showHistoricalBanner = isFullMode && historicalEditEligible;
          if (!showEapiBanner && !showDeliveredBanner && !showHistoricalBanner) return null;
          return (
            <div className={cn("space-y-2 pb-3", isFullMode ? "px-6" : "")}>
              {showEapiBanner && (
                <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950/30 px-3 py-2 text-sm text-yellow-800 dark:text-yellow-200">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>No External Trip ID associated with this order</span>
                </div>
              )}
              {showDeliveredBanner && (
                <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm text-foreground">
                  <OrderStatusBadge status="delivered" />
                  <span>
                    {isAdmin
                      ? "Admin override: this delivered order is fully editable. Changes are audit-logged."
                      : "Only Tender ID can be updated. Documents can still be uploaded. Contact an administrator to adjust other fields."}
                  </span>
                </div>
              )}
              {showHistoricalBanner && (
                <div className="flex items-start gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm text-foreground">
                  <div className="flex-1">
                    <div className="font-medium">
                      Historical / billing-only edit
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {historicalEdit
                        ? "Past-time validation is bypassed and no changes will be sent to the external trip (no pivot, no PATCH, no cancel). Local DB and audit log only."
                        : "External-sync mode is ON. Edits will attempt to pivot/PATCH the external trip and enforce past-time validation. Use only if you actually want EAPI to react to this change."}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-0.5">
                    <Switch
                      id="historical-edit-toggle"
                      checked={historicalEditUserToggle}
                      onCheckedChange={setHistoricalEditUserToggle}
                    />
                    <Label htmlFor="historical-edit-toggle" className="text-xs">
                      Skip external sync
                    </Label>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {/* Tabs - Full mode only */}
        {isFullMode && (
          <div className="flex items-center gap-6 border-b px-6">
            {([
              { value: "details" as const, label: "Details", icon: ClipboardList },
              { value: "history" as const, label: "History", icon: History },
            ]).map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  "flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors",
                  activeTab === tab.value
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* History tab content */}
        {isFullMode && activeTab === "history" && (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <OrderHistory order={order} />
          </div>
        )}

        {/* Details tab / assign mode form */}
        {(!isFullMode || activeTab === "details") && (
        <form onSubmit={handleSubmit} data-order-edit-form className={isFullMode ? "flex flex-col flex-1 overflow-hidden" : "space-y-6"}>
          <div className={isFullMode ? "flex-1 overflow-y-auto px-6 py-4 space-y-6" : "space-y-6"}>
          {/* Order Summary for assign modes */}
          {isAssignMode && (
            <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span>{order.ship_from_city} → {order.ship_to_city}</span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span>
                  {order.ready_time && new Date(order.ready_time).toLocaleDateString()} –{" "}
                  {order.requested_delivery_time && new Date(order.requested_delivery_time).toLocaleDateString()}
                </span>
              </div>
            </div>
          )}

          {/* Status / Billing Party / Trip Type - Full mode only */}
          {isFullMode && (() => {
            const showTripType = !isPerLegTripType(formData);
            return (
            <div className={cn("grid gap-4", showTripType ? "sm:grid-cols-3" : "sm:grid-cols-2")}>
              {/* Status is read-only here. Confirming, marking in transit,
                  delivering and cancelling are explicit actions on the order
                  page — the form only edits order data. */}
              <div>
                <Label>Status</Label>
                <div className="mt-2">
                  <OrderStatusBadge status={displayStatus} />
                </div>
              </div>
              <div>
                <Label>Billing Party</Label>
                <Select
                  value={formData.billing_party_id}
                  onValueChange={(v) =>
                    setFormData((prev) => ({ ...prev, billing_party_id: v }))
                  }
                  disabled={!historicalEdit}
                >
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.organization_name} ({c.short_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {showTripType && (
                <div title={deliveredLocked ? deliveredLockTitle : undefined}>
                  <Label>Trip Type{formData.status === "confirmed" ? " *" : ""}</Label>
                  <Select
                    value={formData.trip_type || ""}
                    onValueChange={handleTripTypeChange}
                    disabled={deliveredLocked}
                  >
                    <SelectTrigger><SelectValue placeholder="Select trip type" /></SelectTrigger>
                    <SelectContent>
                      {tripTypeOptionsForTenant(activeTenant).map((tt) => (
                        <SelectItem key={tt} value={tt}>{tt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            );
          })()}

          {/* Equipment & References - Full mode only */}
          {isFullMode && (() => {
            const perLegAssets = isPerLegAssets(formData);
            return (
            <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {!perLegAssets && (
              <div title={deliveredLocked ? deliveredLockTitle : undefined}>
                <Label htmlFor="trailer-id">Trailer ID</Label>
                <TrailerIdField
                  id="trailer-id"
                  value={formData.trailer_id}
                  baselineValue={order?.trailer_id || ""}
                  onChange={handleTrailerChange}
                  onNeedsServiceBlock={(info) => setNeedsServiceBlock(info)}
                  disabled={deliveredLocked}
                />
              </div>
              )}
              {!perLegAssets && (
              <div title={deliveredLocked ? deliveredLockTitle : undefined}>
                <Label>Trailer Type{trailerTypeRequired ? " *" : ""}</Label>
                <Select
                  value={formData.trailer_type_id || "none"}
                  onValueChange={handleTrailerTypeChange}
                  disabled={deliveredLocked}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {trailerTypes?.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {matched && inheritedTypeLabel && !trailerTypeOverride && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    From checked-in trailer
                  </p>
                )}
                {trailerTypeOverride && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Check-in record will be updated on save
                  </p>
                )}
                {showCustomTrailerTypeReminder && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Defaulted to {getTrailerTypeLabel(customTrailerDefaultTypeId, trailerTypes)} for {originShortCode} departures — please confirm this is the right trailer type.
                  </p>
                )}
              </div>
              )}
              <div>
                <Label htmlFor="tender-id">Tender ID</Label>
                <Input
                  id="tender-id"
                  value={formData.tender_id}
                  onChange={(e) => setFormData((prev) => ({ ...prev, tender_id: e.target.value }))}
                  placeholder="e.g., TND-001"
                />
              </div>
            </div>
            {showAdvanced && formData.order_type === "single-leg" && (
              <div className="mt-4">
                <EquipmentAdvancedFields
                  idPrefix="edit-order"
                  loadStatus={formData.load_status ?? ""}
                  isTemperatureControlled={formData.is_temperature_controlled === true}
                  temperatureF={formData.temperature_f ?? ""}
                  onLoadStatusChange={(v) => setFormData((prev) => ({ ...prev, load_status: v }))}
                  onTemperatureControlledChange={(v) =>
                    setFormData((prev) => ({ ...prev, is_temperature_controlled: v }))
                  }
                  onTemperatureChange={(v) => setFormData((prev) => ({ ...prev, temperature_f: v }))}
                  disabled={deliveredLocked}
                />
              </div>
            )}
            </>
            );
          })()}

          {/* Pending BOL — same visibility rules as the Create form: any mode,
              multi-leg with at least one empty-trailer leg. */}
          <PendingBolCheckbox
            id="edit-pending-bol"
            formData={formData}
            onChange={(next) => setFormData((prev) => ({ ...prev, pending_bol: next }))}
          />


          {/* Per-leg asset toggle (multi-leg only). When checked (default), the
              order-level Tractor/Operator pickers below apply to every leg.
              Per-leg pickers UI lands in a follow-up. */}
          {formData.order_type === "multi-leg" && isFullMode && (() => {
            const sameAssets = formData.use_same_assets_for_all_legs !== false;
            const sameTrip = formData.use_same_trip_type_for_all_legs !== false;
            return (
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="flex items-start gap-2 rounded-lg border border-border bg-accent p-3">
                  <Checkbox
                    id="use_same_assets_for_all_legs"
                    checked={sameAssets}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        use_same_assets_for_all_legs: checked === true,
                      }))
                    }
                  />
                  <div className="grid gap-0.5">
                    <Label
                      htmlFor="use_same_assets_for_all_legs"
                      className="cursor-pointer font-medium"
                    >
                      Use the same tractor, trailer, and operator for all legs.
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {sameAssets
                        ? "Uncheck this option to assign a different tractor, trailer, or operator to each leg."
                        : "Check this option to assign the same tractor, trailer, and operator to all legs."}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-border bg-accent p-3">
                  <Checkbox
                    id="use_same_trip_type_for_all_legs"
                    checked={sameTrip}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        use_same_trip_type_for_all_legs: checked === true,
                      }))
                    }
                  />
                  <div className="grid gap-0.5">
                    <Label
                      htmlFor="use_same_trip_type_for_all_legs"
                      className="cursor-pointer font-medium"
                    >
                      Use the same trip type for all legs.
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {sameTrip
                        ? "Uncheck this option to assign a different trip type to each leg."
                        : "Check this option to assign the same trip type to all legs."}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Stops Section — shared component (full mode only). Rendered above
              Assignments so users set the time window before picking a tractor. */}
          {isFullMode && durationWarnings.length > 0 && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-0.5">
                {durationWarnings.map((w) => (
                  <p key={w.legNumber ?? "order"}>{formatDurationWarning(w)}</p>
                ))}
              </div>
            </div>
          )}
          {isFullMode && (
            <OrderStopsSection
              formData={formData}
              setFormData={setFormData}
              locations={locations}
              allowAddStops={false}
              locationsDisabled={(!!order?.external_order_id && !auryCiOverride) || deliveredLocked}
              timesDisabled={deliveredLocked}
              legTrailerBaselineById={Object.fromEntries(
                ((childLegs ?? []) as any[]).map((l) => [l.id, l.trailer_id || ""]),
              )}
              legLocksByIndex={resolveLegEditLocks(formData, {
                orderStatus: order?.status,
                historicalEdit,
              })}
              tractors={tractors}
              drivers={drivers}
              activeTenant={activeTenant}
              disabledTractorIds={mergedDisabledTractorIds}
              disabledTractorIdsByLeg={disabledTractorIdsByLeg}
              tractorOosStatusById={oosStatusById}
              tractorOosRecordsById={oosRecordsById}
              tractorEligibilityByTractor={eligibilityByTractor}
            tractorEligibilityGateState={eligibilityGateState}
              onTrailerNeedsServiceBlock={(info) => setNeedsServiceBlock(info)}
              showAdvancedFields={showAdvanced}
              nowFloor={
                historicalEdit || formData.is_recurring
                  ? undefined
                  : getNowInPlanningTz()
              }
            />
          )}

          {/* Assignments */}
          <div className={isFullMode ? "grid gap-4 sm:grid-cols-3" : "space-y-4"}>
            {(isFullMode || isAssignTractorMode) && !(isPerLegAssets(formData) && isFullMode) && (
              <div>
                <Label>Assigned Tractor{formData.status === "confirmed" ? " *" : ""}</Label>
                <TractorCombobox
                  tractors={tractors}
                  value={formData.assigned_tractor_id}
                  onChange={handleTractorChange}
                  disabledIds={mergedDisabledTractorIds}
                  eligibilityReasons={eligibilityReasonsById}
                eligibilityGateState={eligibilityGateState}
                  oosStatusById={oosStatusById}
                  disabled={
                    formData.status === "in_transit" ||
                    deliveredLocked ||
                    formData.status === "canceled" ||
                    formData.status === "tender_received"
                  }
                />
                {(formData.status === "in_transit" ||
                  deliveredLocked ||
                  formData.status === "canceled" ||
                  formData.status === "tender_received") && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formData.status === "canceled"
                      ? "Tractor is locked because the order is canceled."
                      : deliveredLocked
                        ? deliveredLockTitle + "."
                        : formData.status === "tender_received"
                          ? "Tractor is locked until the tender is accepted."
                          : "Tractor is locked once the order is in transit."}
                  </p>
                )}
              </div>
            )}
            {(isFullMode || isAssignDriverMode) && !(isPerLegAssets(formData) && isFullMode) && (
              <div title={deliveredLocked ? deliveredLockTitle : undefined}>
                <Label>Primary Operator</Label>
                <DriverCombobox
                  drivers={drivers}
                  value={formData.assigned_operator_id}
                  onChange={(value) => {
                    const newVal = value === "none" ? "" : value;
                    setFormData((prev) => ({
                      ...prev,
                      assigned_operator_id: newVal,
                      ...(newVal ? {} : { assigned_operator_2_id: "" }),
                    }));
                  }}
                  disabled={deliveredLocked}
                />
              </div>
            )}
            {(isFullMode || isAssignDriverMode) && !(isPerLegAssets(formData) && isFullMode) && (
              <div title={deliveredLocked ? deliveredLockTitle : undefined}>
                <Label>Secondary Operator</Label>
                <DriverCombobox
                  drivers={drivers}
                  value={formData.assigned_operator_2_id}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      assigned_operator_2_id: value === "none" ? "" : value,
                    }))
                  }
                  disabled={deliveredLocked || !formData.assigned_operator_id || formData.assigned_operator_id === "none"}
                />
              </div>
            )}
          </div>

          {/* Shipment (advanced) */}
          {isFullMode && showAdvanced && (
            <FormSection
              title="Shipment"
              description="Matches the shipment fields customers send through the Orders API."
            >
              <ShipmentFields
                freightWeightLbs={formData.freight_weight_lbs ?? ""}
                commodityCode={formData.commodity_code ?? ""}
                commodityDescription={formData.commodity_description ?? ""}
                onFreightWeightChange={(v) =>
                  setFormData((prev) => ({ ...prev, freight_weight_lbs: v }))
                }
                onCommodityCodeChange={(v) =>
                  setFormData((prev) => ({ ...prev, commodity_code: v }))
                }
                onCommodityDescriptionChange={(v) =>
                  setFormData((prev) => ({ ...prev, commodity_description: v }))
                }
                disabled={deliveredLocked}
              />
            </FormSection>
          )}
          </div>

          <div className={isFullMode
            ? "sticky bottom-0 z-10 flex justify-end gap-2 border-t bg-background px-6 py-4"
            : "flex justify-end gap-2 pt-4 border-t"}>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {(() => {
              const validation = isFullMode
                ? isOrderFormValid(formData, {
                    isEditing: true,
                    originalFormData: initialFormDataRef.current
                      ? (JSON.parse(initialFormDataRef.current) as OrderFormData)
                      : undefined,
                    historicalEdit,
                  })
                : { valid: true, reasons: [] as string[] };
              const reasons = [...validation.reasons];
              if (
                isFullMode &&
                formData.status === "confirmed" &&
                normalizeOrderStatus(order.status) !== "confirmed" &&
                !!formData.trailer_id?.trim() &&
                requiresOnSite &&
                !matched
              ) {
                reasons.push("Trailer must be checked in on site to confirm.");
              }
              if (!hasChanges && !isAssignMode) reasons.push("No changes to save.");
              return (
                <SubmitWithReasons reasons={reasons} pending={updateMutation.isPending}>
                  {isAssignMode ? "Save Assignment" : "Update Order"}
                </SubmitWithReasons>
              );
            })()}
          </div>

        </form>
        )}


        {/* Trip pivot reason dialog (trip_type / trailer / tractor) */}
        <ChangeReasonDialog {...pivotDialogProps} isPending={updateMutation.isPending} />

        {/* Trip pivot warning dialog */}
        <AlertDialog open={showTractorWarning} onOpenChange={setShowTractorWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Vehicle?</AlertDialogTitle>
              <AlertDialogDescription>
                Changing the vehicle will cancel and create a new external order. The existing external trip will be replaced.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <PivotReasonField
              choice={pivotReasonChoice}
              onChoiceChange={setPivotReasonChoice}
              other={pivotReasonOther}
              onOtherChange={setPivotReasonOther}
              presets={PIVOT_REASON_PRESETS}
            />
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleTractorWarningCancel}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleTractorWarningConfirm} disabled={!pivotReasonValid}>OK</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Trailer pivot warning dialog */}
        <AlertDialog open={showTrailerWarning} onOpenChange={setShowTrailerWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Trailer?</AlertDialogTitle>
              <AlertDialogDescription>
                This order is already confirmed with an external trip. Changing the trailer will cancel the existing external trip and create a new one (Trip Pivot). Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <PivotReasonField
              choice={pivotReasonChoice}
              onChoiceChange={setPivotReasonChoice}
              other={pivotReasonOther}
              onOtherChange={setPivotReasonOther}
              presets={PIVOT_REASON_PRESETS}
            />
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleTrailerWarningCancel}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleTrailerWarningConfirm} disabled={!pivotReasonValid}>Continue</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirmation requires truck + trailer */}
        <AlertDialog open={showConfirmRequiresWarning} onOpenChange={setShowConfirmRequiresWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Truck and Trailer Required</AlertDialogTitle>
              <AlertDialogDescription>
                To confirm an order, you must supply a truck and trailer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setShowConfirmRequiresWarning(false)}>OK</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirmed order cannot have truck/trailer removed */}
        <AlertDialog open={showCannotRemoveWarning} onOpenChange={setShowCannotRemoveWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cannot Remove Truck or Trailer</AlertDialogTitle>
              <AlertDialogDescription>
                A stop with a live external trip can't have its truck or trailer cleared. You can swap it for a different one, or cancel that stop's trip first.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setShowCannotRemoveWarning(false)}>OK</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
      <TrailerCrossTenantDialog
        open={showCrossTenantDialog}
        onOpenChange={(o) => {
          setShowCrossTenantDialog(o);
          if (!o) setCrossTenantMatches([]);
        }}
        trailerIdText={formData.trailer_id.trim()}
        matches={crossTenantMatches}
        onMigrated={() => {
          // Trailer is now in the active tenant — re-run submit, skipping the
          // detection check to avoid a re-prompt.
          setSkipCrossTenantOnce(true);
          setTimeout(() => {
            const form = document.querySelector<HTMLFormElement>(
              "form[data-order-edit-form]",
            );
            form?.requestSubmit();
          }, 0);
        }}
        onCreateSeparate={() => {
          setSkipCrossTenantOnce(true);
          setTimeout(() => {
            const form = document.querySelector<HTMLFormElement>(
              "form[data-order-edit-form]",
            );
            form?.requestSubmit();
          }, 0);
        }}
      />
      <NeedsServiceBlockedDialog
        open={needsServiceBlock !== null}
        onOpenChange={(o) => { if (!o) setNeedsServiceBlock(null); }}
        trailerId={needsServiceBlock?.trailerId ?? ""}
        reason={needsServiceBlock?.reason ?? null}
      />
      <CheckedInTrailerTypeOverrideDialog
        open={pendingTypeChange !== null}
        onConfirm={confirmTrailerTypeOverride}
        onCancel={cancelTrailerTypeOverride}
      />
    </Dialog>
  );
}

interface PivotReasonFieldProps {
  choice: string;
  onChoiceChange: (v: string) => void;
  other: string;
  onOtherChange: (v: string) => void;
  presets: readonly string[];
}

function PivotReasonField({
  choice,
  onChoiceChange,
  other,
  onOtherChange,
  presets,
}: PivotReasonFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor="pivot-reason-select">Reason for pivot</Label>
      <Select value={choice} onValueChange={onChoiceChange}>
        <SelectTrigger id="pivot-reason-select">
          <SelectValue placeholder="Select a reason" />
        </SelectTrigger>
        <SelectContent>
          {presets.map((p) => (
            <SelectItem key={p} value={p}>
              {p}
            </SelectItem>
          ))}
          <SelectItem value="Other">Other</SelectItem>
        </SelectContent>
      </Select>
      {choice === "Other" && (
        <div className="space-y-1">
          <Input
            value={other}
            onChange={(e) => onOtherChange(e.target.value.slice(0, 30))}
            placeholder="Enter reason"
            maxLength={30}
            autoFocus
          />
          <div className="text-right text-xs text-muted-foreground">
            {other.length}/30
          </div>
        </div>
      )}
    </div>
  );
}
